import { motion } from "framer-motion";

const COLORS = ["#FF5D1F", "#FFE600", "#FF2A85", "#00E5FF", "#7C3AED", "#10B981", "#FFFFFF", "#FFB800"];
const DARK_TEXT = new Set(["#FFE600", "#00E5FF", "#FFFFFF", "#FFB800"]);
const R = 92;

const point = (deg: number, r = R) => [r * Math.sin((deg * Math.PI) / 180), -r * Math.cos((deg * Math.PI) / 180)];
const short = (s: string) => (s.length > 13 ? `${s.slice(0, 12)}…` : s);

function segments(count: number, weights?: number[]) {
  const w = Array.from({ length: count }, (_, i) => Math.max(0.1, weights?.[i] ?? 1));
  const total = w.reduce((a, b) => a + b, 0);
  let acc = 0;
  return w.map((x) => {
    const start = (acc / total) * 360;
    acc += x;
    return { start, end: (acc / total) * 360 };
  });
}

export function wheelRotation(labels: string[], result: string, spins: number, weights?: number[]) {
  const segs = segments(labels.length, weights);
  const s = segs[Math.max(0, labels.indexOf(result))];
  return spins * 360 + (360 - (s.start + s.end) / 2);
}

interface WheelProps { labels: string[]; result: string; fast: boolean; className?: string; duration?: number; weights?: number[] }

export function PrizeWheel({ labels, result, fast, className = "h-56 w-56 sm:h-64 sm:w-64", duration = 1.25, weights }: WheelProps) {
  const segs = labels.length >= 2 ? labels : [result, result];
  const w = labels.length >= 2 ? weights : undefined;
  const arcs = segments(segs.length, w);
  return (
    <div className={`relative ${className}`} data-testid="prize-wheel-anim">
      <div className="absolute left-1/2 top-[-10px] z-10 h-0 w-0 -translate-x-1/2 border-x-[14px] border-t-[26px] border-x-transparent border-t-ink" aria-hidden />
      <motion.svg viewBox="-100 -100 200 200" className="h-full w-full drop-shadow-[4px_4px_0_#121212]"
        initial={{ rotate: 0 }} animate={{ rotate: wheelRotation(segs, result, fast ? 0 : 5, w) }}
        transition={{ duration: fast ? 0 : duration, ease: [0.15, 0.85, 0.25, 1] }}>
        <circle r={R + 5} fill="#121212" />
        {segs.map((label, i) => {
          const { start, end } = arcs[i];
          const [x1, y1] = point(start);
          const [x2, y2] = point(end);
          const color = COLORS[i % COLORS.length];
          const mid = (start + end) / 2;
          return (
            <g key={i}>
              <path d={`M0 0 L${x1} ${y1} A${R} ${R} 0 ${end - start > 180 ? 1 : 0} 1 ${x2} ${y2} Z`} fill={color} stroke="#121212" strokeWidth={2.5} />
              <text transform={`rotate(${mid}) translate(0 -54) rotate(90)`} textAnchor="middle" dominantBaseline="middle"
                fontFamily="Fredoka, sans-serif" fontWeight={700} fontSize={segs.length > 6 ? 8.5 : 10} fill={DARK_TEXT.has(color) ? "#121212" : "#FFFFFF"}>
                {short(label)}
              </text>
            </g>
          );
        })}
        <circle r={14} fill="#FFE600" stroke="#121212" strokeWidth={4} />
      </motion.svg>
    </div>
  );
}
